import { useCallback, useEffect, useRef, useState } from 'react';
import ReactMapGL, { MapProps, MapRef } from 'react-map-gl/mapbox';
import debounce from 'lodash/debounce';
import { EasingOptions, Map } from 'mapbox-gl';

import { Skeleton } from '@hcs/design-system';
import {
  useParentDimensions,
  usePrevious,
  useRerender,
  useScreenSize,
} from '@hcs/hooks';
import {
  ControlConfig,
  FitBoundsPayload,
  HeatmapColorTheme,
  LayersControlConfig,
  MapOutlineConfig,
  MapPreferences,
  MapStyles,
  MlsCoveragePayload,
  PropertyTypeEnum,
  VectilesMetricGroups,
  VectilesMetricIds,
} from '@hcs/types';
import { ENV } from '@hcs/webapps';

import { MAPBOX_KEY } from '../../constants';
import { useHcMap } from '../../hooks';
import { INITIAL_ZOOM } from '../../rtk';
import { fitGeoLocationsToViewPort } from '../../utils';
import { ClusterToggle } from '../ClusterToggle';
import { HcMapDevTool } from '../HcMapDevTool';
import { HcMapLayers } from '../HcMapLayers';
import { MlsCoverageLayer } from '../HcMapLayers';
import { HcMapLayersControl } from '../HcMapLayersControl';
import { SatelliteToggle } from '../SatelliteToggle';
import { ZoomControl } from '../ZoomControl';

import { HcMapOutline } from './HcMapOutline';

import styles from './HcMap.module.css';

const TRANSITION_DURATION = 300;
const DEBOUNCE_WAIT = 30;
const DEFAULT_FLYTO_OPTIONS: EasingOptions = {
  zoom: 16,
  padding: {
    bottom: 80,
    top: 0,
    left: 0,
    right: 0,
  },
  curve: 1.1,
  speed: 1.9,
};

export interface HcMapProps extends MapProps {
  mapId: string;
  dataHcName: string;
  dataHcEventSection?: string;
  className?: string;
  loading?: boolean;
  triggerHideLayersControl?: number;
  mapStyle?: MapStyles;
  mapOutlineConfigs?: MapOutlineConfig[];
  vectilesMetricId?: VectilesMetricIds;
  vectilesMetricGroup?: VectilesMetricGroups;
  propertyType?: PropertyTypeEnum | null;
  layersControl?: LayersControlConfig;
  zoomControl?: ControlConfig;
  satelliteControl?: ControlConfig;
  clusterControl?: ControlConfig;
  fitBounds?: Omit<FitBoundsPayload, 'mapId'>;
  fitBoundsMaxZoom?: number;
  mlsCoverage?: Omit<MlsCoveragePayload, 'mapId'>;
  mapPreferences?: MapPreferences;
  heatmapTheme?: HeatmapColorTheme;
  onZoomControlChange?: (zoom: number) => void;
}

export const HcMap = ({
  dataHcName,
  dataHcEventSection,
  mapId,
  className,
  propertyType,
  fitBounds,
  fitBoundsMaxZoom,
  loading,
  layersControl,
  mapOutlineConfigs,
  zoomControl,
  mapStyle,
  onZoomControlChange,
  clusterControl,
  satelliteControl,
  vectilesMetricGroup,
  vectilesMetricId,
  mlsCoverage,
  heatmapTheme = HeatmapColorTheme.Solutions,
  triggerHideLayersControl,
  ...mapProps
}: HcMapProps) => {
  const [mapReady, setMapReady] = useState(false);
  const [isMapStateMounted, setIsMapStateMounted] = useState<boolean>(false);
  const {
    mapState,
    actions: {
      hcMapViewportChange,
      hcMapMount,
      hcMapLayersChangePropertyType,
      hcMapChangeZoom,
      hcMapFitBoundsToGeoLocations,
      hcMapChangeVectilesMetric,
      hcMapChangeVectilesMetricGroup,
      hcMapFlyTo,
      hcMapSetIsMoving,
      hcMapSetHeatmapTheme,
    },
  } = useHcMap(mapId);

  const { isPhoneWidth } = useScreenSize();
  useEffect(() => {
    if (!mapState?.isMapMoving) {
      hcMapSetIsMoving({ mapId, isMapMoving: true });
    }
    // Debounce the setting of isMapMoving so the user has time to interact with the map
    const debounceTimeout = setTimeout(() => {
      hcMapSetIsMoving({ mapId, isMapMoving: false });
    }, 500);
    return () => {
      clearTimeout(debounceTimeout);
    };
    // This effect depends only on the map bounds
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Reset the timeout when the map bounds are changing
    mapState?.boundsInfo?.northEast.lat,
    mapState?.boundsInfo?.northEast.lng,
    mapState?.boundsInfo?.southWest.lat,
    mapState?.boundsInfo?.southWest.lng,
  ]);
  const { children, onMove, latitude, longitude } = mapProps;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapRef | null>(null);
  const parentDimensions = useParentDimensions({
    ref: containerRef,
  });
  const previousParentDimensions = usePrevious(parentDimensions);
  const debouncedResize = useCallback(
    debounce((map: Map | undefined) => {
      map?.resize();
    }, DEBOUNCE_WAIT),
    [],
  );

  const attemptMapFlyTo = useCallback(() => {
    if (mapRef.current && mapState?.flyTo) {
      mapRef.current.flyTo({
        ...DEFAULT_FLYTO_OPTIONS,
        ...mapState.flyTo,
        essential: true, // this animation is considered essential with respect to prefers-reduced-motion
      });
      // Reset flyTo state
      hcMapFlyTo({
        mapId,
        flyTo: undefined,
      });
    }
  }, [mapState?.flyTo]);

  const mapRefCallback = useCallback(
    (map: MapRef | null) => {
      if (map) {
        mapRef.current = map;
      }
      attemptMapFlyTo();
    },
    [mapRef, attemptMapFlyTo],
  );

  useEffect(() => {
    attemptMapFlyTo();
  }, [attemptMapFlyTo]);

  // Rerender if parent isn't ready
  useRerender({
    shouldRerender: !parentDimensions?.height || !parentDimensions.width,
    max: 1,
  });

  useEffect(() => {
    if (
      (mapRef.current !== null &&
        parentDimensions?.width &&
        parentDimensions?.height &&
        parentDimensions?.width !== previousParentDimensions?.width) ||
      parentDimensions?.height !== previousParentDimensions?.height
    ) {
      debouncedResize(mapRef.current?.getMap());
    }
  }, [mapRef, debouncedResize, parentDimensions, previousParentDimensions]);

  // useEffects to allow props to control the hcMapSlice
  useEffect(() => {
    if (
      !isMapStateMounted &&
      parentDimensions?.width &&
      parentDimensions?.height &&
      !loading
    ) {
      let viewport = mapState && mapState.viewport;
      const partialViewport = {
        height: parentDimensions?.height,
        width: parentDimensions?.width,
        zoom: mapProps.zoom || INITIAL_ZOOM,
        latitude,
        longitude,
      };
      // Calculate viewport from fitBounds prop
      if (fitBounds) {
        const fitResult = fitGeoLocationsToViewPort(
          fitBounds.coords,
          {
            viewport: partialViewport,
          },
          fitBounds.padding || 50,
        );
        if (fitResult) {
          viewport = {
            latitude: fitResult.coords.lat,
            longitude: fitResult.coords.lng,
            height: fitResult.viewport.height,
            width: fitResult.viewport.width,
            zoom:
              fitBoundsMaxZoom && fitResult.viewport.zoom > fitBoundsMaxZoom
                ? fitBoundsMaxZoom
                : fitResult.viewport.zoom,
          };
        }
        // Calculate viewport from latitude and longitude props
      } else if (latitude && longitude) {
        viewport = {
          ...partialViewport,
          latitude,
          longitude,
        };
      }
      // Exit without mounting map/setting mapState if we still can't determine initial map location
      if (!viewport || !viewport.latitude || !viewport.longitude) {
        return;
      }
      hcMapMount({
        mapId,
        mapState: mapState
          ? {
              ...mapState,
              fitId: fitBounds?.fitId,
              viewport,
              mlsCoverage,
              heatmapTheme: heatmapTheme,
              heatmap: {
                ...mapState.heatmap,
                vectilesMetricId: vectilesMetricId || null,
                vectilesMetricGroup: vectilesMetricGroup || null,
              },
            }
          : {
              viewport,
              fitId: fitBounds?.fitId,
              heatmap: {
                vectilesMetricId: vectilesMetricId || null,
                vectilesMetricGroup: vectilesMetricGroup || null,
                showMonochrome: false,
                propertyType: propertyType,
              },
              markers: {
                showMarkers: true,
              },
              mapStyle: mapStyle || MapStyles.Default,
              heatmapTheme: heatmapTheme,
              mlsCoverage,
            },
      });
      setIsMapStateMounted(true);
      // Note: purposely excluding many props from deps to only run this useEffect on mount. Other
      // useEffects will handle moving the map/updating state when those props change.
      // Do not add hcMapMount to deps else cause infinite re render
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentDimensions, loading]);

  // Change map position when latitude/longitude props change
  useEffect(() => {
    if (isMapStateMounted && latitude && longitude) {
      hcMapViewportChange({
        mapId,
        viewport: {
          latitude,
          longitude,
        },
      });
      // Do not add hcMapViewportChange to deps else cause infinite re render
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, latitude, longitude]);

  // Support changing map layers externally
  useEffect(() => {
    if (isMapStateMounted) {
      hcMapChangeVectilesMetric({
        mapId,
        vectilesMetricId: vectilesMetricId || null,
      });
    }
    // Intentionally exclude isMapStateMounted from deps. This should only run when props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, vectilesMetricId]);

  // Support changing HeatmapColorTheme externally
  useEffect(() => {
    if (heatmapTheme && isMapStateMounted) {
      hcMapSetHeatmapTheme({
        mapId,
        heatmapTheme,
      });
    }
    // Intentionally exclude isMapStateMounted from deps. This should only run when props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapId, heatmapTheme]);

  // Support changing map layer groups externally
  useEffect(() => {
    if (isMapStateMounted) {
      hcMapChangeVectilesMetricGroup({
        mapId,
        vectilesMetricGroup: vectilesMetricGroup || null,
      });
    }
  }, [mapId, vectilesMetricGroup]);

  useEffect(() => {
    if (isMapStateMounted) {
      hcMapLayersChangePropertyType({
        mapId,
        propertyType: propertyType,
        // Do not add hcMapLayersChangePropertyType to deps else cause infinite re render
      });
    }
    // Intentionally exclude isMapStateMounted from deps. This should only run when props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyType, mapId]);

  useEffect(() => {
    if (
      fitBounds?.coords.length &&
      fitBounds.fitId &&
      fitBounds.fitId !== mapState?.fitId &&
      isMapStateMounted
    ) {
      hcMapFitBoundsToGeoLocations({ mapId, ...fitBounds });
      // Do not add hcMapFitBoundsToGeoLocations to deps else cause infinite re render
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitBounds, isMapStateMounted, mapId]);

  // cleanup debounced functions on unmount
  useEffect(() => {
    return () => {
      debouncedResize.cancel();
    };
  }, [debouncedResize]);
  return (
    <div
      data-hc-name={dataHcName}
      data-hc-event-section={dataHcEventSection}
      className={styles.HcMap}
      ref={containerRef}
    >
      {/* Map ready indicator for playwright tests */}
      {mapReady && <div data-testid="map-ready" />}
      {loading || !isMapStateMounted ? (
        <Skeleton dataHcName={`${dataHcName}-skeleton`} />
      ) : (
        <ReactMapGL
          {...mapProps}
          {...mapState?.viewport}
          onZoom={(event) => {
            const { zoom } = event.viewState;
            hcMapChangeZoom({
              mapId,
              zoom,
            });
          }}
          key={mapId}
          ref={mapRefCallback}
          onIdle={(event) => {
            setMapReady(true);
            mapProps?.onIdle?.(event);
          }}
          mapStyle={mapState?.mapStyle || MapStyles.Default}
          mapboxAccessToken={MAPBOX_KEY}
          onMove={(event) => {
            const viewState = event.viewState;
            onMove?.(event);
            const { latitude, longitude, zoom } = viewState;

            if (latitude && longitude && zoom) {
              hcMapViewportChange({
                mapId,
                viewport: {
                  latitude,
                  longitude,
                  zoom,
                  width: parentDimensions?.width,
                  height: parentDimensions?.height,
                },
              });
            }
          }}
          attributionControl={false}
        >
          {mapOutlineConfigs &&
            mapOutlineConfigs.map((outlineConfig) => (
              <HcMapOutline key={outlineConfig.id} {...outlineConfig} />
            ))}
          {zoomControl && (
            <ZoomControl
              zoom={mapState?.viewport.zoom || INITIAL_ZOOM}
              position={zoomControl.position}
              offsetLeft={zoomControl.offsetLeft}
              offsetTop={
                // Account for mls coverage notice at the top of the map on phone sizes
                mapState?.mlsCoverage?.warningVisible && isPhoneWidth
                  ? (zoomControl.offsetTop || 0) + 80
                  : zoomControl.offsetTop
              }
              duration={TRANSITION_DURATION}
              mapId={mapId}
              onChange={onZoomControlChange}
            />
          )}
          {satelliteControl && (
            <SatelliteToggle
              mapId={mapId}
              mapStyle={mapState?.mapStyle || MapStyles.Default}
              position={satelliteControl.position}
              offsetLeft={satelliteControl.offsetLeft}
              offsetTop={satelliteControl.offsetTop}
            />
          )}
          {clusterControl && (
            <ClusterToggle
              mapId={mapId}
              className={styles.ClusterToggle}
              position={clusterControl.position}
            />
          )}
          {children}
          {layersControl && (
            <HcMapLayersControl
              mapId={mapId}
              {...layersControl}
              triggerHide={triggerHideLayersControl}
            />
          )}
          {mapState?.heatmap.vectilesMetricId && <HcMapLayers mapId={mapId} />}
          {mapState?.mlsCoverage?.visible && <MlsCoverageLayer mapId={mapId} />}
          {ENV !== 'prod' && <HcMapDevTool mapId={mapId} />}
        </ReactMapGL>
      )}
    </div>
  );
};
